import type { PaymentSystem } from '~/api/Order';
import type { PaymentMethods } from '~/types/payments';

export enum CompanyName {
  CityDrinks = 'citydrinks',
  Localee = 'localee',
  CircleK = 'circlek',
  Vilo = 'vilo',
  Jiffy = 'jiffy',
  Kids = 'ourkids',
  Electronium = 'electronium',
  Oskuhus = 'oskuhus',
  Heimkaup = 'heimkaup',
  Cerise = 'cerise',
  DubaiExpress = 'dubaiexpress',
  DubaiExpressCom = 'dubaiexpresscom',
  Storrd = 'storrd',
  MCosmetic = 'mcosmetic',
  Ocado = 'ocado',
}

export enum TimeFormat {
  h12 = 'h12',
  h24 = 'h24',
}

export type EnvVariables = {
  REACT_APP_MIDTRANS_KEY: string;
  REACT_APP_MIDTRANS_URL: string;
  REACT_APP_INTERCOM_ID: string;
  REACT_APP_API_ROOT: string;
  REACT_APP_TRACKING_MAP_API_URL: string;
  REACT_APP_CLOUDFLARE_IMAGE_RESIZE_DOMAIN: string;
  REACT_APP_CLOUDFLARE_IMAGE_RESIZE_DOMAIN_OLD: string;
  REACT_APP_CLOUDFLARE_IMAGE_RESIZE_ENABLED: string;
  REACT_APP_AWS_PAY_BASE_URL: string;
  REACT_APP_AWS_PAY_PHRASE: string;
  REACT_APP_AWS_PAY_RETURN_URL: string;
  REACT_APP_AWS_PAY_ACCESS_CODE: string;
  REACT_APP_MERCHANT_IDENTIFIER: string;
  REACT_APP_PREDEFINED_CAPTCHA_TOKEN: string;
  REACT_APP_STRIPE_API_KEY: string;
  REACT_APP_BI_API_KEY: string;
  REACT_APP_TURNSTILE_SITE_KEY: string;
  REACT_APP_ADYEN_CLIENT_KEY: string;
  REACT_APP_ENCRYPTION_KEY: string;
  REACT_APP_VERIFONE_SCRIPT_URL: string;
  REACT_APP_CARDINAL_SCRIPT_URL: string;
  REACT_APP_DEFAULT_WAREHOUSE?: string;
  REACT_APP_GTM_ID?: string;
  REACT_APP_GA_MEASUREMENT_ID?: string;
};

export type YaMetrikaConfig = {
  production: string;
  test?: string;
  useEcommercePayload?: boolean;
};

export type CompanyConfig = {
  id: string;
  lang: string;
  langs: string[];
  name: string;
  nameSecondary: string;
  currency: string;
  currencyCode: string;
  officialName: string;
  marketingEmail?: string;
  email: string;
  feedbackEmail?: string;
  phonePlaceholder: string;
  defaultPhoneCode?: string;
  contactEmail?: string;
  phoneNumber?: string;
  address?: string;
  canonical: string;
  ageRestricted: number;
  timeZone: string;
  minimalOrderFeeThreshold: number;
  useGoogleApiForGeocoding?: boolean;
  useAutocompleteAddress?: boolean;
  countryCode?: string;
  /** Tax label shown in cart, checkout and order totals. Defaults to `VAT`. */
  taxName?: string;
  /** Preset cash amounts offered on the checkout cash-payment step. */
  cashBanknotes?: number[];
  defaultVolumeDisplay: string;
  workingHours?: {
    start: string;
    end: string;
  };
  warehouse: {
    default: string;
    minimalOrderFeeWarehouseCodes: string[];
    location: GeoCoordinates;
  };
  /** Shown until the address is picked; its coordinates are `warehouse.location`. */
  defaultCity?: string;
  map: {
    center: GeoCoordinates;
    zoom: number;
    markers?: {
      position: GeoCoordinates;
      label?: string;
    }[];
  };
  paymentMethods: PaymentMethods[];
  availablePaymentMethods?: (keyof typeof PaymentSystem)[];
  timeFormat: TimeFormat;
  links: {
    terms?: string;
    privacy?: string;
    cookie: string;
    deliveryZones: string;
    forRiders: string;
    affiliateProgram: string;
    blog?: string;
    /** Base URL of the external courier tracking page; the order number is appended to it. */
    deliveryTracking?: string;
    legals?: {
      [key: string]: {
        label: string;
        link: string;
        blank?: boolean;
      };
    };
    whatsapp?: string;
    facebook?: string;
    instagram?: string;
    tiktok?: string;
    youtube?: string;
    linkedin?: string;
    twitter?: string;
    tg?: string;
    supportTg?: string;
    vk?: string;
    // [key: string]: string;
  };
  showCookieBanner?: boolean;
  /** Data shown on the public Contacts page. */
  contacts?: {
    /** Customer support working hours block. Hidden when omitted. */
    workingHours?: {
      /** Opening time, formatted for display (e.g. '9:00', '9:00 AM'). */
      start: string;
      /** Closing time, formatted for display (e.g. '21:00', '9:00 PM'). */
      end: string;
      /** Timezone abbreviation shown next to the hours (e.g. 'MSK', 'GST'). */
      timezone: string;
    };
    /** Office address lines. Each entry is rendered as a separate line. Hidden when omitted or empty. */
    officeAddress?: string[];
  };
  referral: {
    code: string;
    discount: string;
    minBasket: string;
    downloadAppLink: string;
  };
  points?: {
    value?: string;
    pointsPerUnit?: number;
    icon: 'coins' | 'jiffy-points';
  };
  appStoreUrl: {
    ios: string;
    android: string;
  };
  apis: {
    yaMetrika?: YaMetrikaConfig;
  };
  license: {
    name: string;
    numberPrinary: string;
    numberSecondary?: string;
  };
};

/**
 * All feature flag getters in the Company class.
 * JSDoc descriptions are parsed by CLI tools (scripts/lib/feature-registry.ts).
 */
export interface CompanyFeatures {
  // Main Page
  /** Welcome block on the main page (currently unused in components) */
  readonly hasWelcomeBlock: boolean;
  /** Express delivery banner on main page, adjusts header/menu positioning */
  readonly showExpressDeliveryBannerMainPage: boolean;
  /** Company logo above header on mobile main page */
  readonly showCompanyLogoOnMainMobile: boolean;
  /** "Start Shopping" category item in the home page category list */
  readonly showStartShopping: boolean;
  /** "Order Again" recommendation slider with previously purchased products */
  readonly showOrderAgainSlider: boolean;
  /** "Top Picks" recommendation slider with popular products */
  readonly showTopPicksSlider: boolean;

  // Product Page
  /** Tab navigation (Product/Characteristics/Reviews) on mobile product page */
  readonly showSectionsMobileProductPage: boolean;
  /** Search input in the product page header on mobile */
  readonly showSearchHeaderProductPage: boolean;
  /** Share and favorite buttons over the product image on mobile */
  readonly showActionButtonsMobileProductPage: boolean;
  /** Short list of recent reviews (up to 2) in mobile product info */
  readonly showShortReviewListMobileProductPage: boolean;
  /** Product properties (producer, region) and description in characteristics */
  readonly showDescriptionReviewProductPage: boolean;
  /** Express delivery badge on product cards, cart cards, product page and product modal (NEGATED: adding DISABLES) */
  readonly showExpressBadge: boolean;
  /** Product cards show the discount badge only, dropping every other system badge */
  readonly showOnlyDiscountBadgeOnProductCards: boolean;
  /** Payment card info disclaimer on checkout payment methods screen */
  readonly showCardDisclaimer: boolean;
  /** Saved OCTO cards on checkout: request, render selector, send pay_by_saved_card */
  readonly isOctoSavedCardsEnabled: boolean;
  /** Matching product recommendations on the product detail page */
  readonly showMatchingProducts_ProductPage: boolean;
  /** Similar product recommendations on the product detail page */
  readonly showSimilarProducts_ProductPage: boolean;
  /** "Frequently bought together" recommendations on the product detail page */
  readonly showFrequentlyBoughtTogether_ProductPage: boolean;

  // Cart
  /** Detailed subtotal component (CartCostItems) instead of simple text in mobile cart */
  readonly showCartMobileSubtotal: boolean;
  /** ID verification description text below checkout button on mobile */
  readonly showCartAdditionalTextMobile: boolean;
  /** Count of selected items next to delete button in cart header */
  readonly showCartCoutToRemove: boolean;
  /** Combine delivery items into single delivery by default at checkout */
  readonly combineItemsByDefaut: boolean;
  /** Per-SKU cap on the number of units a customer may add to the cart;
   *  `undefined` means no company-specific cap (only stock/backorder limits apply) */
  readonly maxQuantityPerSku: number | undefined;

  // Delivery
  /** Auto-select scheduled delivery slot instead of express at checkout */
  readonly isExpressDeliveryOffByDefault: boolean;
  /** Allow empty address type field (skip defaulting to 'home') */
  readonly canDefaultAddressBeEmpty: boolean;
  /** Address flow is the delivery/pickup modal: no location-name field, so points are named by street + house */
  readonly usesDeliveryPickupAddressModal: boolean;
  /** In-store QR codes carry a warehouse code that preselects that store as the pickup address */
  readonly hasPickupStoreLink: boolean;
  /** Use new EditAddressLocation modal flow instead of legacy DeliveryAddress */
  readonly showNewAddressModal: boolean;
  /** Address field in the V3 edit-address modal accepts typing with Google Places suggestions */
  readonly canSearchAddress: boolean;
  /** Helper subtitle text in address editing modal */
  readonly showAddressSubtitle: boolean;
  /** Express delivery banner in header that toggles with address display */
  readonly showHeaderExpressBanner: boolean;
  /** Only allow PUDO point selection on map, disable address geocoding and center pin */
  readonly isPudoOnly: boolean;
  /** Tenant is allowed to run courier delivery: frontend half of the gate, and who asks the backend for its config */
  readonly isCourierDeliveryFeatureEnabled: boolean;
  /** Delivery type switcher (courier / PUDO) in the address modal: feature flag AND backend `isCourierDeliveryEnabled` */
  readonly hasDeliveryTypeSwitcher: boolean;
  /** Permanent delivery address restriction banner on checkout page */
  readonly showCheckoutAddressRestrictionBanner: boolean;

  // UI
  /** Master flag for design system v3: new layouts, styling, UI patterns */
  readonly newDesignSystemEnabled: boolean;
  /** App download promotional banner on mobile web header */
  readonly showMobileDowloadPanel: boolean;
  /** New visual design for onboarding page (container and image styling) */
  readonly hasNewOnboardingDesign: boolean;
  /** Progress indicator dots above action buttons in onboarding bottom sheet */
  readonly hasOnboardingDotsAboveButtons: boolean;
  /** Mini footer (FooterMini) instead of standard footer during checkout */
  readonly hasNewCheckoutElemtnts: boolean;
  /** Show App Store / Google Play download buttons block in desktop footer instead of QR code */
  readonly isFooterAppDownloadButtonsEnabled: boolean;
  /** Show "Favs" (favorites) tab in mobile bottom navigation bar */
  readonly isFavsTabEnabled: boolean;
  /** App update required modal for React Native clients at checkout */
  readonly isUpdateRequiredModalEnabled: boolean;
  // Business
  /** KYC identity verification required before completing orders */
  readonly needVerification: boolean;
  /** Integrate favorites section into user cabinet (personal area) */
  readonly isFavsInCabinet: boolean;
  /** IndexNow SEO: msvalidate.01 meta tag for search engine crawling */
  readonly isEnabledIndexNow: boolean;
  /** Intercom support chat: SDK script + identity-verification API */
  readonly isEnabledIntercom: boolean;
  /** Sommelier AI recommendation widget in search and header */
  readonly isSommelierEnabled: boolean;
  /** Delivery fee info tooltip/modal icon in checkout summary */
  readonly isDeliveryInfoModalEnabled: boolean;
  /** "Refer Friend" caption text in order summary share block */
  readonly isShareBlockCaptionVisible: boolean;
  /** Skip age restriction modal for age-restricted cart products */
  readonly missAgeRestrictionModal: boolean;
  /** Treats Tobacco/Alcohol commodity groups as age-restricted on the frontend
   *  (independent of backend `properties.age_restriction` flag) */
  readonly useCommodityGroupAgeCheck: boolean;
  /** GDPR cookie consent banner shown on the first visit */
  readonly showCookieBanner: boolean;

  // Header
  /** Controls orders icon/dropdown visibility in HeaderV3 (NEGATED: adding HIDES) */
  readonly showHeaderOrders: boolean;
  /** Controls delivery address display in HeaderV3 (NEGATED: adding HIDES) */
  readonly showHeaderAddress: boolean;
  /** Controls user action buttons visibility in HeaderV3 (NEGATED: adding HIDES) */
  readonly showHeaderUserActions: boolean;
  /** Controls promotional banners section in HeaderV3 */
  readonly showHeaderBanners: boolean;

  // Company Config
  /** Hides cabinet (personal area) routes for marketplace companies */
  readonly hideCabinetRoutes: boolean;
  /** Uses company default language instead of browser/cookie language */
  readonly preferCompanyLanguage: boolean;
  /** Falls back to default warehouse when product has no warehouse */
  readonly useDefaultWarehouseWhenMissing: boolean;
  /** Displays prices as integers without decimal places */
  readonly useIntegerCurrency: boolean;
  /** Shows feedback widget inside products grid on catalog page */
  readonly insertGridFeedbackInProductsGrid: boolean;

  // Logistics
  /** Uses LMS orders/v3 calculate and new-order flow with route selection */
  readonly hasNewLogistics: boolean;
  /** Hides map on order page and shows barcode in mobile header instead */
  readonly hideOrderMap: boolean;
  /**
   * Whether the tenant has the geofencing checkout warning available. The
   * actual popup also requires a positive `geofencingDistanceThreshold` on
   * the user's current delivery zone (see `useGeofencingCheck`).
   */
  readonly isGeofencingEnabled: boolean;
  /**
   * Presents the geofencing warning as a passive notice inside the checkout
   * address card instead of the blocking `ModalGeofencing` bottom sheet.
   * Requires `isGeofencingEnabled` — this flag only picks the presentation.
   */
  readonly showGeofencingNotice: boolean;
  /** Customer email is optional: forms accept an empty value and the order is created without `recipient.email` */
  readonly isCustomerEmailOptional: boolean;
  /**
   * Adds `interactive-widget=overlays-content` to the viewport meta: the
   * software keyboard overlays the page instead of resizing the visual
   * viewport. Side effect — the browser stops scrolling the focused input
   * into view, so the tenant's layouts must handle that themselves.
   */
  readonly keyboardOverlaysContent: boolean;
}
